// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { arrForEach, arrIndexOf, createCachedValue, createIterator, getLength, isFunction, isObject, isUndefined, iterForOf, objCreate, objDefine, objDefineProps, objForEachKey, objIs, objKeys, safe, strSplit } from "@nevware21/ts-utils";
import { _DYN_ATTRIBUTE_COUNT_LIMI5, _DYN_ENTRIES, _DYN_ERROR_HANDLERS, _DYN_JOIN, _DYN_PUSH, _DYN_SPLICE, _DYN_SPLIT, _DYN_TRACE_CFG } from "../../__DynamicConstants";
import { STR_EMPTY, UNDEFINED_VALUE } from "../../constants/InternalConstants";
import { handleAttribError } from "../../internal/handleErrors";
var _inheritedKey = "~[[inherited]]";
var _deletedKey = "~[[deleted]]";
var _containerId = 0;
function _noOpFunc() {
    // No-op function
}
function _addValue(container, target, theKey, value, maxAttribs, cfg) {
    var errorHandlers = cfg[_DYN_ERROR_HANDLERS /* @min:%2eerrorHandlers */] || {};
    if (theKey && getLength(theKey) > 0) {
        var key = theKey[_DYN_SPLIT /* @min:%2esplit */](".");
        var parts = [];
        var keyIndex = 0;
        var keyLen = getLength(key);
        while (keyIndex < keyLen) {
            var part = key[keyIndex];
            parts[_DYN_PUSH /* @min:%2epush */](part);
            if (keyIndex === keyLen - 1) {
                // last part
                if (target[part] && target[part].n) {
                    // This node already exists as a branch node
                    handleAttribError(errorHandlers, "Attribute key [" + parts[_DYN_JOIN /* @min:%2ejoin */](".") + "] already exists as a branch node", theKey, value);
                    return { r: 2 /* AddAttributeResult.BranchNodeExists */ };
                }
                if (!target[part] || target[part].d) {
                    // Node doesn't exist or was deleted
                    if (container.size >= maxAttribs) {
                        // If the key is not already present, we have exceeded the limit
                        // But if it does exist in the hierarchy, we can replace it locally
                        if (!container.has(theKey)) {
                            handleAttribError(errorHandlers, "Maximum allowed attributes exceeded [" + maxAttribs + "]", theKey, value);
                            return { r: 1 /* AddAttributeResult.MaxAttribsExceeded */ };
                        }
                    }
                    // Add new leaf node or restore deleted node
                    target[part] = {
                        v: value
                    };
                    return { r: 0 /* AddAttributeResult.Success */, a: true };
                }
                else {
                    // replace the value - capture the previous value
                    var previousValue = target[part].v;
                    target[part].v = value;
                    target[part].d = false; // Clear any deleted flag when setting value
                    return { r: 0 /* AddAttributeResult.Success */, a: false, p: previousValue };
                }
            }
            if (!target[part]) {
                target[part] = {
                    n: {}
                };
            }
            if (!target[part].n) {
                handleAttribError(errorHandlers, "Attribute key [" + parts[_DYN_JOIN /* @min:%2ejoin */](".") + "] already exists as a leaf node", theKey, value);
                return { r: 3 /* AddAttributeResult.LeafNodeExists */ };
            }
            target = target[part].n;
            keyIndex++;
        }
    }
    handleAttribError(errorHandlers, "Attribute key is empty", theKey, value);
    return { r: 4 /* AddAttributeResult.EmptyKey */ };
}
/**
 * Delete a specific attribute from the tree
 * @param target - The target branch to delete from
 * @param key - The key parts to delete
 * @returns true if the attribute was deleted, false if it didn't exist
 */
function _deleteValue(target, key) {
    if (key && getLength(key) > 0) {
        var keyIndex = 0;
        var keyLen = getLength(key);
        // Navigate to the parent of the target node, creating path if needed
        while (keyIndex < keyLen - 1) {
            var part = key[keyIndex++];
            if (!target[part]) {
                target[part] = {
                    n: {}
                };
            }
            if (!target[part].n) {
                // Path is blocked by a leaf node, can't delete
                return { d: false };
            }
            target = target[part].n;
        }
        // Now we're at the parent of the target node
        var lastPart = key[keyLen - 1];
        if (target[lastPart] && !target[lastPart].d && !target[lastPart].n) {
            // Node exists, is not already deleted, and is a leaf - delete it
            var prev = target[lastPart].v;
            target[lastPart].d = true; // Mark as deleted
            target[lastPart].v = UNDEFINED_VALUE; // Clear the value
            return { d: true, p: prev };
        }
        else if (!target[lastPart] || !target[lastPart].d) {
            // Node doesn't exist or is not already deleted - create a deleted marker
            // This is important for tracking inherited keys that are locally deleted
            // and for the edge case where inherited keys might be added later
            target[lastPart] = {
                d: true // Mark as deleted without a value
            };
            return { d: true };
        }
    }
    return { d: false };
}
/**
 * Find a local specific node (including if it's been deleted) in the attribute tree, or if it exists in an inherited source
 * @param container - The target attribute container to search in
 * @param key - The key to search for
 * @param filter - The filter to apply
 * @param cb - The callback function to execute when the node is found
 * @param nodes - The attribute branches to search in
 * @param inheritContainer - The inherited attribute container
 * @param inheritAttribObj - The inherited attribute object
 * @param otelCfg - Optional OpenTelemetry configuration
 * @returns The result of the callback function
 */
function _findDetail(container, key, filter, cb, nodes, inheritContainer, inheritAttribObj, otelCfg) {
    var theDetail;
    // Find the local node
    if (key && getLength(key) > 0) {
        var theNode = void 0;
        var target = nodes;
        var keys = strSplit(key, ".");
        var keysLen = getLength(keys);
        var idx = 0;
        if (filter == 0 /* eAttributeFilter.Local */ || filter === 2 /* eAttributeFilter.LocalOrDeleted */ || isUndefined(filter)) {
            // Find any local node (if it exists)
            while (target && idx < keysLen) {
                var part = keys[idx++];
                var node = target[part];
                if (node && idx >= keysLen) {
                    // last part
                    theNode = node;
                    break;
                }
                if (!node || !node.n) {
                    // TODO - support wildcard(s) (when no node is found), likely need to refactor this to support multiple keys
                    break;
                }
                target = node.n;
            }
            // So if the caller didn't specify a filter then always return the found "value" (which will be undefined for a deleted key)
            // If they have specified a filter, then we need to respect that filter where Local and LocalOrDeleted are treated the same
            if (theNode) {
                theDetail = {
                    c: container,
                    k: key,
                    n: theNode,
                    v: theNode.d ? UNDEFINED_VALUE : theNode.v,
                    s: 0 /* eAttributeSource.Local */,
                    e: !theNode.d
                };
            }
        }
        // So we get here if
        // - We didn't find a node (no local overrides)
        // - The filter doesn't match (undefined, Local, LocalOrDeleted), so we didn't look for a node
        // So we just need to check if the filter is undefined or inherited and if so we are free
        // to look in the inherited sources
        if (!theDetail && (filter === 1 /* eAttributeFilter.Inherited */ || isUndefined(filter))) {
            var inheritedValue = _getInheritedValue(key, inheritContainer, inheritAttribObj);
            if (inheritedValue) {
                theDetail = {
                    c: container,
                    k: key,
                    n: null,
                    v: inheritedValue.v,
                    s: 1 /* eAttributeSource.Inherited */,
                    e: true
                };
            }
        }
    }
    return cb(theDetail);
}
function _size(target, inheritAttrib, inheritContainer) {
    var count = 0;
    var seenKeys = [];
    // Use the iterator to count all unique attributes including inheritance
    var iter = _iterator(target, function (prefix, key, _node) {
        return prefix + key;
    }, undefined, inheritContainer || inheritAttrib);
    iterForOf(iter, function (key) {
        if (arrIndexOf(seenKeys, key) === -1) {
            seenKeys[_DYN_PUSH /* @min:%2epush */](key);
            count++;
        }
    });
    return count;
}
function _getStackEntry(theNode, prefix) {
    var entry;
    if (theNode) {
        var allKeys = objKeys(theNode);
        if (getLength(allKeys) > 0) {
            entry = {
                p: prefix,
                n: theNode,
                k: allKeys,
                i: -1
            };
        }
    }
    return entry;
}
function _iterator(target, cb, otelCfg, parentAttribs, inclDeleted) {
    var stack = [];
    var visitedKeys = parentAttribs ? [] : UNDEFINED_VALUE;
    var inheritState;
    var inheritContainerIter;
    var current = _getStackEntry(target, STR_EMPTY); // Start with the root branch
    var inheritContainer;
    var inheritAttribs;
    // Used as the initializer for the iterator and the flag to indicate that the iterator is done
    var ctx = {
        v: undefined,
        n: _moveNext
    };
    if (parentAttribs) {
        if (isAttributeContainer(parentAttribs)) {
            inheritContainer = parentAttribs;
        }
        else if (isObject(parentAttribs)) {
            inheritAttribs = parentAttribs;
        }
    }
    function _moveNext() {
        var thePrefix = STR_EMPTY;
        var theKey;
        var theNode;
        var theSource = 0 /* eAttributeFilter.Local */; // Default to local
        // Process entries from the current node
        while (current) {
            current.i++;
            if (current.i < getLength(current.k)) {
                // We have at least 1 key
                var key = current.k[current.i];
                // The key is "real" (we don't support null or undefined keys)
                var node = current.n[key];
                if (node) {
                    if (node.n) {
                        // We are at a branch node, so add the current node to the stack so we can continue traversing it later
                        stack[_DYN_PUSH /* @min:%2epush */](current);
                        current = _getStackEntry(node.n, current.p + key + ".");
                        // If the branch is empty, continue with the next item from stack
                        if (!current) {
                            current = stack.pop();
                        }
                    }
                    else {
                        // Leaf node
                        var fullKey = current.p + key;
                        if (visitedKeys && arrIndexOf(visitedKeys, fullKey) === -1) {
                            visitedKeys[_DYN_PUSH /* @min:%2epush */](fullKey);
                        }
                        // If we are not deleted, then return this key
                        if (!node.d || inclDeleted) {
                            // Leaf node with value - not deleted
                            thePrefix = current.p;
                            theKey = key;
                            theNode = node;
                            theSource = node.d ? 2 /* eAttributeFilter.LocalOrDeleted */ : 0 /* eAttributeFilter.Local */;
                            break;
                        }
                    }
                }
            }
            else {
                // No more keys to process at this level
                current = stack.pop();
            }
        }
        // We get here only after we have processed all keys of the current instance
        // Switch to processing inherited attributes if we have them
        if (!theNode && inheritAttribs) {
            if (!inheritState) {
                // Lazy initialize the inherited state
                inheritState = {
                    p: STR_EMPTY,
                    n: UNDEFINED_VALUE,
                    k: objKeys(inheritAttribs),
                    i: -1
                };
            }
            // Process inherited attributes that haven't been overridden or deleted
            while (inheritState.i < getLength(inheritState.k) - 1) {
                inheritState.i++;
                var key = inheritState.k[inheritState.i];
                if (arrIndexOf(visitedKeys, key) === -1) {
                    visitedKeys[_DYN_PUSH /* @min:%2epush */](key);
                    theKey = key;
                    theNode = { v: inheritAttribs[key] };
                    theSource = 1 /* eAttributeFilter.Inherited */;
                    break;
                }
            }
        }
        // Process inherited container if we have one and no other node was found
        if (!theNode && inheritContainer) {
            if (!inheritContainerIter) {
                // Initialize the container's iterator only once - this will include the full inheritance chain
                inheritContainerIter = inheritContainer[_DYN_ENTRIES /* @min:%2eentries */]();
            }
            // Use the container's iterator to get next entry
            iterForOf(inheritContainerIter, function (entry) {
                var key = entry[0];
                if (arrIndexOf(visitedKeys, key) === -1) {
                    visitedKeys[_DYN_PUSH /* @min:%2epush */](key);
                    theKey = key;
                    theNode = { v: entry[1] };
                    theSource = 1 /* eAttributeFilter.Inherited */; // Always return Inherited for inherited container entries
                    return -1;
                }
            });
        }
        if (theNode) {
            ctx.v = cb(thePrefix, theKey, theNode, theSource);
        }
        return !theNode;
    }
    return createIterator(ctx);
}
function _generateAttributes(container, target, otelCfg, inheritAttrib, showTree) {
    // TODO: Look at making this return a proxy instead of a new object
    // This should be more effecient as it would make the property lookups lazy
    var attribs = objCreate(null);
    var deletedKeys;
    if (showTree) {
        // Add Node Id
        objDefine(attribs, "#id", {
            v: container.id,
            w: false
        });
    }
    // Use the iterator properly - collect the key-value pairs
    var iter = _iterator(target, function (prefix, key, node, source) {
        var name = prefix + key;
        return { n: name, v: node.v, d: node.d, s: source };
    }, otelCfg, showTree ? null : inheritAttrib, showTree ? 2 /* eAttributeFilter.LocalOrDeleted */ : UNDEFINED_VALUE);
    iterForOf(iter, function (entry) {
        var theNode = attribs;
        var theName = entry.n;
        if (entry.d) {
            if (!deletedKeys) {
                // Create separate objects for inherited and deleted attributes
                deletedKeys = objCreate(null);
                objDefine(attribs, _deletedKey, {
                    v: deletedKeys,
                    w: false
                });
            }
            theNode = deletedKeys;
        }
        if (theNode && !(theName in theNode)) {
            theNode[theName] = entry.v;
        }
    });
    if (inheritAttrib && showTree) {
        // Create separate objects for inherited and deleted attributes
        var inheritedKeys_1;
        // Add a tree showing the inherited attributes
        if (isAttributeContainer(inheritAttrib)) {
            inheritedKeys_1 = inheritAttrib._attributes;
        }
        else {
            // Create separate objects for inherited and deleted attributes
            inheritedKeys_1 = objCreate(null);
            objForEachKey(inheritAttrib, function (key, value) {
                inheritedKeys_1[key] = value;
            });
        }
        objDefine(attribs, _inheritedKey, {
            v: inheritedKeys_1,
            w: false
        });
    }
    return attribs;
}
function _notifyListeners(listeners, changeInfo) {
    // Notify all registered listeners of changes
    if (listeners) {
        arrForEach(listeners, function (listener) {
            safe(listener.cb, [changeInfo]);
        });
    }
}
function _getInheritedValue(key, inheritContainer, inheritAttribObj) {
    // Check inherited container first
    if (inheritContainer && inheritContainer.has(key)) {
        return { v: inheritContainer.get(key) };
    }
    // Then check inherited attributes object
    if (inheritAttribObj && key in inheritAttribObj) {
        return { v: inheritAttribObj[key] };
    }
}
function _createUnloadHook(listeners, callback) {
    var cbInst = {
        cb: callback
    };
    listeners[_DYN_PUSH /* @min:%2epush */](cbInst);
    var unloadHook = {
        rm: function () {
            if (listeners && cbInst) {
                var index = arrIndexOf(listeners, cbInst);
                if (index >= 0) {
                    // Remove the current listener
                    listeners[_DYN_SPLICE /* @min:%2esplice */](index, 1);
                }
                // Clear the cached values
                cbInst.cb = null;
                cbInst = null;
                // Optimization to drop all references and shortcut any future lookups
                unloadHook.rm = _noOpFunc;
            }
        }
    };
    return unloadHook;
}
/**
 * Creates a new attribute container that provides an efficient, observable key-value storage
 * for OpenTelemetry attributes with support for inheritance, limits, and change notifications.
 *
 * The container supports inherited attributes from parent containers or plain objects,
 * enforces attribute count and value size limits, and provides efficient iteration and access patterns.
 *
 * @param otelCfg - The OpenTelemetry configuration containing trace configuration and limits
 * @param name - Optional name for the container (used in the container ID)
 * @param inheritAttrib - Optional parent attributes or container to inherit from
 * @param attribLimits - Optional specific attribute limits to override configuration defaults
 * @returns A new IAttributeContainer instance with the specified configuration
 * @since 3.4.0
 * @example
 * ```typescript
 * const config = { traceCfg: { generalLimits: { attributeCountLimit: 64 } } };
 * const container = createAttributeContainer(config, "my-container");
 * container.set("service.name", "my-service");
 *
 * // With inheritance
 * const parent = { "environment": "production" };
 * const child = createAttributeContainer(config, "child-container", parent);
 * console.log(child.get("environment")); // "production"
 * ```
 */
export function createAttributeContainer(otelCfg, name, inheritAttrib, attribLimits) {
    var traceCfg = otelCfg[_DYN_TRACE_CFG /* @min:%2etraceCfg */] || {};
    var nodes = null;
    var theSize = null;
    var theDropped = null;
    var limits = traceCfg.generalLimits || {};
    var maxAttribs = limits[_DYN_ATTRIBUTE_COUNT_LIMI5 /* @min:%2eattributeCountLimit */] || 128;
    // let maxValueLen: number = limits.attributeValueLengthLimit;
    var theAttributes;
    var localAttributes;
    var droppedAttribs = 0;
    var inheritContainer = null;
    var inheritAttribObj = null;
    var listeners = null;
    var parentListenerHook = null;
    var containerName = name || STR_EMPTY;
    if (attribLimits) {
        maxAttribs = attribLimits[_DYN_ATTRIBUTE_COUNT_LIMI5 /* @min:%2eattributeCountLimit */] || maxAttribs;
        // maxValueLen = attribLimits.attributeValueLengthLimit || maxValueLen;
    }
    // Determine if inheritAttrib is a container or plain attributes object
    if (isAttributeContainer(inheritAttrib)) {
        inheritContainer = inheritAttrib;
    }
    else if (isObject(inheritAttrib)) {
        inheritAttribObj = inheritAttrib;
    }
    inheritAttrib = null;
    var inheritSrc = inheritAttribObj || inheritContainer;
    var container = {
        id: (containerName || STR_EMPTY) + "." + (_containerId++),
        size: 0,
        droppedAttributes: 0,
        attributes: UNDEFINED_VALUE,
        clear: function () {
            // Remove parent listener if exists
            if (parentListenerHook) {
                parentListenerHook.rm();
                parentListenerHook = null;
            }
            // Only inform children if we appear to have any keys or could possible inherit keys
            if (nodes || inheritContainer || inheritAttribObj) {
                // Inform any children (Synchronously) that we are about to clear all attributes
                // Called prior to clearing the nodes, so that children still have full access
                // to the current and inherited attributes
                _notifyListeners(listeners, { frm: container.id, op: 0 /* eAttributeChangeOp.Clear */ });
            }
            nodes = null;
            theSize = null;
            theDropped = null;
            theAttributes = null;
            localAttributes = null;
            droppedAttribs = 0;
            inheritSrc = null; // Clear the inherited attributes
            inheritContainer = null; // Clear inherited container as well
            inheritAttribObj = null; // Clear inherited attributes as well
        },
        get: function (key, source) {
            return _findDetail(container, key, source, function (detail) {
                // Just return the value (which will include deleted ones (as undefined)if the filter is set)
                return detail ? detail.v : UNDEFINED_VALUE;
            }, nodes, inheritContainer, inheritAttribObj, otelCfg);
        },
        has: function (key, source) {
            return _findDetail(container, key, source, function (detail) {
                // Note: We may still have a detail object if the key was deleted, so we need to
                // - Check if the detail is considered to "exist"
                // - If the source filter is LocalOrDeleted, then return that is "exists"
                return detail ? (detail.e || source === 2 /* eAttributeFilter.LocalOrDeleted */) : false;
            }, nodes, inheritContainer, inheritAttribObj, otelCfg);
        },
        set: function (key, value) {
            if (!nodes) {
                // Lazily create a container object
                nodes = objCreate(null);
            }
            var addResult = _addValue(container, nodes, key, value, maxAttribs, otelCfg);
            if (addResult.r === 0 /* AddAttributeResult.Success */) {
                theSize = null; // invalidate any previously cached size
                theAttributes = null; // invalidate any previously cached attributes
                localAttributes = null; // invalidate any previously cached local attributes
                // Determine operation type based on whether this was a new attribute or set
                var op = addResult.a ? 2 /* eAttributeChangeOp.Add */ : 1 /* eAttributeChangeOp.Set */;
                var prevValue = addResult.p;
                if (op === 2 /* eAttributeChangeOp.Add */) {
                    // Special case, if we just added/changed it locally we need to lookup our parents to see if we are replacing (hiding)
                    // a previously inherited value and if so we need to notify our children that this was a set not an add (if the value changed)
                    var inheritedValue = _getInheritedValue(key, inheritContainer, inheritAttribObj);
                    if (inheritedValue) {
                        op = 1 /* eAttributeChangeOp.Set */;
                        prevValue = inheritedValue.v;
                    }
                }
                // Only notify children if the value has changed
                if (!objIs(value, prevValue)) {
                    // Inform any children (Synchronously) that we have "changed" a value
                    // Unlike clear, this is called after the change is made, so children
                    // will need to "handle" the change accordingly
                    _notifyListeners(listeners, {
                        frm: container.id,
                        op: op,
                        k: key,
                        prev: prevValue,
                        val: value
                    });
                }
            }
            else if (addResult.r === 1 /* AddAttributeResult.MaxAttribsExceeded */) {
                theDropped = null;
                droppedAttribs++;
                _notifyListeners(listeners, {
                    frm: container.id,
                    op: 4 /* eAttributeChangeOp.DroppedAttributes */,
                    k: key,
                    val: value
                });
            }
            return addResult.r === 0 /* AddAttributeResult.Success */;
        },
        del: function (key) {
            // Find the node / value for this container
            return _findDetail(container, key, UNDEFINED_VALUE, function (detail) {
                // Check if the key exists locally (incl marked as deleted) or is inherited
                if (detail) {
                    // So the key exists either locally or in an inherited source
                    // - It may also exists but marked as deleted
                    if (!nodes) {
                        // Always create nodes structure if it doesn't exist since we need to track deleted keys
                        nodes = objCreate(null);
                    }
                    // Now lets mark this key as deleted locally
                    var deleteResult = _deleteValue(nodes, key[_DYN_SPLIT /* @min:%2esplit */]("."));
                    if (deleteResult.d) {
                        theSize = null; // invalidate any previously cached size
                        theAttributes = null; // invalidate any previously cached attributes
                        localAttributes = null; // invalidate any previously cached local attributes
                    }
                    // Inform any children (Synchronously) that we have deleted a value
                    // This is important even if the key didn't exist locally, as it existed in inheritance
                    // chain and we need children (like snapshot containers) to know about the deletion to
                    // ignore any future inherited values.
                    _notifyListeners(listeners, {
                        frm: container.id,
                        op: 3 /* eAttributeChangeOp.Delete */,
                        k: key,
                        prev: detail.v
                    });
                    // Return true if we successfully marked it as deleted
                    return deleteResult.d;
                }
                return false;
            }, nodes, inheritContainer, inheritAttribObj, otelCfg);
        },
        keys: function () {
            return _iterator(nodes, function (prefix, key, node, source) {
                return prefix + key;
            }, otelCfg, inheritSrc);
        },
        entries: function () {
            return _iterator(nodes, function (prefix, key, node, source) {
                return [prefix + key, node.v, source];
            }, otelCfg, inheritSrc);
        },
        values: function () {
            return _iterator(nodes, function (_prefix, _key, node, _source) {
                return node.v;
            }, otelCfg, inheritSrc);
        },
        forEach: function (cb) {
            var iter = _iterator(nodes, function (prefix, key, node, source) {
                cb(prefix + key, node.v, source);
                return true; // Return true to indicate the callback was executed
            }, otelCfg, inheritSrc);
            // Iterate over the entire container
            iterForOf(iter, _noOpFunc);
        },
        child: function (name, snapshot) {
            var childName = (name ? name : "child") + (snapshot ? "<-@[" : "<=[") + container.id + "]";
            return snapshot ? _createSnapshotContainer(otelCfg, childName, container, attribLimits) : createAttributeContainer(otelCfg, childName, container, attribLimits);
        },
        listen: function (callback) {
            if (!listeners) {
                listeners = [];
            }
            return _createUnloadHook(listeners, callback);
        }
    };
    function _listener(changeInfo) {
        // Invalidate caches when parent changes
        var shouldNotify = true;
        // If a parent adds a new key, then if this instance has not replaced it
        // then we need to also inform our children
        if (changeInfo.op === 2 /* eAttributeChangeOp.Add */ || changeInfo.op === 1 /* eAttributeChangeOp.Set */ || changeInfo.op === 3 /* eAttributeChangeOp.Delete */) {
            theSize = null;
            theAttributes = null;
            localAttributes = null;
            // If we already have (or deleted) this key locally, then we don't need to notify children
            _findDetail(container, changeInfo.k, 2 /* eAttributeFilter.LocalOrDeleted */, function (detail) {
                // If we have a node (which also might be that it was deleted) then don't notify
                // If we didn't find a node then we still need to notify our children
                shouldNotify = !detail || !detail.n;
            }, nodes);
        }
        else if (changeInfo.op === 0 /* eAttributeChangeOp.Clear */) {
            theSize = null;
            theAttributes = null;
            localAttributes = null;
            // If the parent clears all of it's keys, we need to inform our children
            shouldNotify = true;
        }
        else if (changeInfo.op === 4 /* eAttributeChangeOp.DroppedAttributes */) {
            shouldNotify = true;
            theDropped = null;
        }
        if (shouldNotify) {
            _notifyListeners(listeners, changeInfo);
        }
    }
    // If we have a parent container, register a listener to stay connected to parent changes
    if (inheritContainer && isFunction(inheritContainer.listen)) {
        parentListenerHook = inheritContainer.listen(_listener);
    }
    return objDefineProps(container, {
        size: {
            g: function () {
                if (!theSize) {
                    theSize = createCachedValue(_size(nodes, inheritAttribObj, inheritContainer));
                }
                return theSize.v;
            }
        },
        droppedAttributes: {
            g: function () {
                if (!theDropped) {
                    theDropped = createCachedValue((inheritContainer ? inheritContainer.droppedAttributes : 0) + droppedAttribs);
                }
                return theDropped.v;
            }
        },
        attributes: {
            g: function () {
                if (!theAttributes) {
                    theAttributes = createCachedValue(_generateAttributes(container, nodes, otelCfg, inheritSrc));
                }
                return theAttributes.v;
            }
        },
        _attributes: {
            g: function () {
                if (!localAttributes) {
                    localAttributes = createCachedValue(_generateAttributes(container, nodes, otelCfg, inheritSrc, true));
                }
                return localAttributes.v;
            }
        }
    });
}
/**
 * Add all attributes from the source attributes or container to the target container.
 * This function has performance and memory implications as it immediately copies all key-value pairs
 * from the source to the target container, handling both plain attribute objects and other attribute
 * containers.
 *
 * @param container - The target container to add attributes to
 * @param attributes - The source attributes or container to copy from
 * @since 3.4.0
 * @example
 * ```typescript
 * const target = createAttributeContainer(config, "target");
 * const source = { key1: "value1", key2: "value2" };
 * addAttributes(target, source);
 *
 * // Or from another container
 * const sourceContainer = createAttributeContainer(config, "source");
 * sourceContainer.set("key3", "value3");
 * addAttributes(target, sourceContainer);
 * ```
 */
export function addAttributes(container, attributes) {
    if (isAttributeContainer(attributes)) {
        // Use the container's forEach for direct processing - more efficient than entries() iterator
        attributes.forEach(function (key, value, source) {
            container.set(key, value);
        });
    }
    else if (attributes) {
        if (isFunction(attributes[_DYN_ENTRIES /* @min:%2eentries */])) {
            // Handle any type that has an entries function
            iterForOf(attributes[_DYN_ENTRIES /* @min:%2eentries */](), function (entry) {
                container.set(entry[0], entry[1]);
            });
        }
        else {
            // Handle as plain attributes object
            objForEachKey(attributes, function (key, value) {
                container.set(key, value);
            });
        }
    }
}
function _createSnapshotContainer(otelCfg, name, sourceContainer, attribLimits) {
    var newContainer = createAttributeContainer(otelCfg, name, sourceContainer, attribLimits);
    sourceContainer.listen(function (changeInfo) {
        if (changeInfo.op === 0 /* eAttributeChangeOp.Clear */) {
            // Copy all parent keys to this container if not already present
            iterForOf(sourceContainer[_DYN_ENTRIES /* @min:%2eentries */](), function (entry) {
                var key = entry[0];
                // If the Key has not been set or explicitly deleted from the new Container, and the source Container
                // has the key (directly or inherited), then copy the value locally so we have a copy because our parent
                // some grand parent is clearing their collection of values. Note ideal as if it's a grand parent or higher
                // we end up duplicating the keys multiple times, but we only know about our direct ancestor
                // Note: Deleting locally hides any inherited value
                if (!newContainer.has(key, 2 /* eAttributeFilter.LocalOrDeleted */) && sourceContainer.has(key)) {
                    newContainer.set(key, entry[1]);
                }
            });
        }
        else if (changeInfo.op === 2 /* eAttributeChangeOp.Add */) {
            // For add operations add), we need to ensure that when the key is not already present on the current instance
            // that we hide the key so it's "not" inherited, by marking it as deleted.
            var key = changeInfo.k;
            if (!newContainer.has(key, 0 /* eAttributeFilter.Local */)) {
                newContainer.del(key);
            }
        }
        else if (changeInfo.op === 1 /* eAttributeChangeOp.Set */ || changeInfo.op === 3 /* eAttributeChangeOp.Delete */) {
            // For set operations (change), preserve the previous value if we don't have it locally
            // For delete operations, if we don't have the key locally, preserve it with the deleted value
            var key = changeInfo.k;
            if (!newContainer.has(key, 2 /* eAttributeFilter.LocalOrDeleted */)) {
                newContainer.set(key, changeInfo.prev);
            }
        }
    });
    return newContainer;
}
/**
 * Creates a snapshot container based on the passed IOTelAttributes or IAttributeContainer.
 * The returned container effectively treats the source attributes as immutable as at the time of creation, you
 * may still add / update existing attributes without affecting the original source. And changes to the source
 * attributes / container will not be reflected from the new snapshot container, only changes made to the returned
 * container itself.
 *
 * Note: It implements this in a lazy manner, so changes made to the original source after the snapshot is taken will
 * cause the changed attributes to be copied into this snapshot container with it's original value and cause a local
 * version of the changed key (if not already been present) to be added, so when using the {@link IAttributeContainer#has} or
 * {@link IAttributeContainer#get} methods, with the optional source filter as {@link eAttributeFilter.Inherited} it will
 * only return attributes that were present and unchanged at the time of the snapshot. This means for those attributes you must
 * use the {@link eAttributeFilter.Local} as source filter (or leave it undefined) to access the local version.
 *
 * It is recommended that you always use {@link IAttributeContainer} instances for better memory and performance overheads,
 * for this specific function when you pass a {@link IOTelAttributes} instance it will create a copy of all present attributes
 * at the point of creation (not lazily).
 *
 * @param otelCfg - The OpenTelemetry configuration to use for the snapshot container
 * @param source - The source attributes or container to create a snapshot view from
 * @param attribLimits - Optional attribute limits to apply to the snapshot container
 * @returns An IAttributeContainer instance that will preserves the attributes and values from the source attributes / container
 * at creation time. The snapshot container will be named "snapshot(xxxx)" where xxxx is the source container ID, or "snapshot(...)" for non-container sources.
 * @since 3.4.0
 * @example
 * ```typescript
 * // Immediate copy for plain attributes
 * const attrs = { key1: "value1", key2: "value2" };
 * const snapshot = createAttributeSnapshot(attrs);
 * // snapshot.id will be "snapshot(...).N"
 * attrs.key1 = "changed"; // snapshot.get("key1") is still "value1"
 *
 * // Lazy copy-on-change for containers
 * const container = createAttributeContainer(config, "my-container");
 * container.set("key1", "value1");
 * const snapshot2 = createAttributeSnapshot(container);
 * // snapshot2.id will be "snapshot(my-container.N).M"
 * container.set("key1", "changed"); // snapshot2.get("key1") remains "value1" (previous value copied)
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function createAttributeSnapshot(otelCfg, name, source, attribLimits) {
    var newContainer;
    if (isAttributeContainer(source)) {
        newContainer = _createSnapshotContainer(otelCfg, (name || "child") + "<-@[" + source.id + "]", source, attribLimits);
    }
    else {
        newContainer = createAttributeContainer(otelCfg, (name || "child") + "<-@[(...)]", null, attribLimits);
        addAttributes(newContainer, source);
    }
    return newContainer;
}
/**
 * Helper function to identify if a passed argument is or implements the IAttributeContainer interface
 * @param value - The value to check
 * @returns true if the value implements IAttributeContainer interface, false otherwise
 * @since 3.4.0
 * @example
 * ```typescript
 * const container = createAttributeContainer(config, "test-container");
 * if (isAttributeContainer(container)) {
 *     // TypeScript now knows container is IAttributeContainer
 *     console.log(container.size);
 *     container.set("key", "value");
 * }
 *
 * // Check unknown object
 * function processContainer(obj: unknown) {
 *     if (isAttributeContainer(obj)) {
 *         obj.forEach((key, value) => console.log(key, value));
 *     }
 * }
 * ```
 */
/*#__NO_SIDE_EFFECTS__*/
export function isAttributeContainer(value) {
    return value &&
        isFunction(value.clear) &&
        isFunction(value.get) &&
        isFunction(value.has) &&
        isFunction(value.set) &&
        isFunction(value.del) &&
        isFunction(value.keys) &&
        isFunction(value[_DYN_ENTRIES /* @min:%2eentries */]) &&
        isFunction(value.forEach) &&
        isFunction(value.values) &&
        isFunction(value.child) &&
        isFunction(value.listen) &&
        (isObject(value) || isFunction(value)) &&
        // Check for existence of the required properties, but don't cause them to be processed ("executed")
        ("id" in value) &&
        ("size" in value) &&
        ("droppedAttributes" in value) &&
        ("attributes" in value);
}
//# sourceMappingURL=attributeContainer.js.map